"use client"

import styled from "@emotion/styled"
import { GetPageQuery } from "apollo"
import { BlockRenderer, Container, CustomBreadcrumbs, TopBanner } from "ui"
import { extractProperty, findProperty, getBreadCrumbs, submitSuccess } from "utils"
import MainHeader from "./MainHeader"
import TitleContent from "./TitleContent"

import { yupResolver } from "@hookform/resolvers/yup"
import { LegacyRef, useRef, useState } from "react"
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form"
import FormStep from "./FormStep"
import { IFormInput, schema } from "./form/FormData"

import { postWebContactUsSendDonationRequest } from "api"
import { DonationMessages } from "app/[locale]/(layout)/donation-application-form/page"
import ReCAPTCHA from "react-google-recaptcha"
import FailModal from "./FailModal"
import SuccessModal from "./SuccessModal"

const StyledBreadCrumbsContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 80px;
    margin-bottom: 40px;
  }
`

const StyledForm = styled.form`
  ${(props) => props.theme.breakpoints.down("sm")} {
    .grecaptcha-badge {
      height: 45px !important;
    }
  }
`

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

type Props = {
  data: GetPageQuery
  locale: string
  messages: DonationMessages
}
function DonationApplication({ data, locale, messages }: Props) {
  const { selfObject, parents } = getBreadCrumbs(data, locale)
  const components = {
    topBanner: TopBanner,
    pgeMainTitle: MainHeader,
    basicTitleAndTextBlock: TitleContent,
    formStepTitle: FormStep,
  }
  const [formIsSubmitted, setFormIsSubmitted] = useState(false)
  const reCaptchaRef = useRef<ReCAPTCHA | null>()

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setError,
    trigger,
    getFieldState,
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
    mode: "onBlur",
  })

  const [formFiles, setFormFiles] = useState<any>({
    PartCCeoSignedRequestLetterWithLawyerApproved: null,
    PartCTaxExemption: null,
    PartCIRSApprovalBookKeeping: null,
    PartCNonProfitsRegistrationManagementApproval: null,
    PartCIncomeTaxSection46: null,
    PartCAssociationTerms: null,
  })

  const isStepAValid = () => {
    if (
      getFieldState("PartAFromNonProfitOrganization").isDirty &&
      getFieldState("PartANotMarketingPromotion").isDirty &&
      getFieldState("PartAAssociationDetailsName").isDirty &&
      getFieldState("PartAAssociationDetailsNumber").isDirty &&
      getFieldState("PartAAssociationDetailsPhone").isDirty &&
      getFieldState("PartAAssociationDetailsWebsite").isDirty &&
      getFieldState("PartAAssociationDetailsCeo").isDirty &&
      getFieldState("PartAAmount").isDirty &&
      getFieldState("PartAVolunteeringOptions").isDirty &&
      getFieldState("PartAContactUsDetailsName").isDirty &&
      getFieldState("PartAContactUsDetailsContactRole").isDirty &&
      getFieldState("PartAContactUsDetailsPhone").isDirty &&
      getFieldState("PartAContactUsDetailsEmail").isDirty &&
      getFieldState("PartAActivityAndGoals").isDirty &&
      getFieldState("PartAIsAshtromGroupConnection").isDirty &&
      getFieldState("PartAAshtromGroupConnection").isDirty &&
      Object.keys(errors).length === 0
    ) {
      return true
    } else return false
  }
  const isStepBValid = () => {
    if (
      getFieldState("PartBDescription").isDirty &&
      getFieldState("PartBProjectGoals").isDirty &&
      getFieldState("PartBTargetAudienceName").isDirty &&
      getFieldState("PartBTargetAudienceSupportingPeopleAmount").isDirty &&
      getFieldState("PartBArea").isDirty &&
      getFieldState("PartBProjectSchedule").isDirty &&
      getFieldState("PartBChangesToAchieve").isDirty &&
      getFieldState("PartBProjectSuccessYourOpinion").isDirty &&
      Object.keys(errors).length === 0
    ) {
      return true
    } else return false
  }
  const isStepCValid = () => {
    // if (
    //   isStringValueValid(
    //     watch("PartCCeoSignedRequestLetterWithLawyerApproved")
    //   ) &&
    //   isStringValueValid(watch("PartCTaxExemption")) &&
    //   isStringValueValid(watch("PartCIRSApprovalBookKeeping")) &&
    //   isStringValueValid(
    //     watch("PartCNonProfitsRegistrationManagementApproval")
    //   ) &&
    //   isStringValueValid(watch("PartCIncomeTaxSection46")) &&
    //   isStringValueValid(watch("PartCAssociationTerms"))
    // ) {
    //   return true
    // } else return false

    const res = Object.values(formFiles).every((i: any) => i?.name?.includes(".pdf"))
    return res
  }
  const [activeAcordion, setAtiveAcordion] = useState(0)

  const findFailedFields = (string: string, data: any) => {
    let foundFirst: boolean | string = false
    for (const key in data) {
      // Use Object.prototype.hasOwnProperty.call instead of data.hasOwnProperty
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (string.includes(key)) {
          // @ts-ignore
          setError(key, { type: string, message: string })
          if (!foundFirst) {
            foundFirst = key
          }
        }
      }
    }
    if (typeof foundFirst === "string") {
      const accordionIndex = foundFirst.includes("stepA")
        ? 3
        : foundFirst.includes("stepB")
        ? 4
        : foundFirst.includes("stepC")
        ? 5
        : null
      if (typeof accordionIndex === "number") {
        setAtiveAcordion(accordionIndex)
      }
    }
  }

  const [errorOnFiles, setErrorOnFiles] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const onSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    if (!isStepCValid()) {
      setErrorOnFiles(true)
      setTimeout(() => {
        const firstError = document.querySelector<HTMLElement>(`.Mui-error, [aria-invalid="true"]`)
        firstError?.focus()
        firstError?.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 100)
      return
    }

    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
    try {
      const obj = { ...data, ...formFiles, GRecaptchaResponse: recaptchaResponse }
      const formData = new FormData()
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          formData.append(key, obj[key])
        }
      }
      postWebContactUsSendDonationRequest(formData)
        .then((res: any) => {
          if (res?.data?.errorMessage) {
            findFailedFields(res?.data?.errorMessage, obj)
          }
          if (res?.data?.body) {
            submitSuccess("שליחה", "הגשת בקשה לתרומה לפרויקט המקדם חוסן קהילתי", true)
            setSuccess(true)
          }
        })
        .catch((err) => {
          console.error("error", err)
        })
    } catch (error) {
      setSuccess(false)
      setFormIsSubmitted(true)
    }
  }
  const onError: SubmitErrorHandler<IFormInput> = (err) => {
    console.error(err)
    setTimeout(() => {
      const firstError = document.querySelector<HTMLElement>(`[aria-invalid="true"]`)
      firstError?.focus()
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 100)
  }

  const blocks = findProperty(data.page, "blocks")?.blocks || []
  const hasBanner = blocks?.some(
    (block: any) => block.blockType === "topBanner" || block.blockType === "HeaderWithHeroAndText",
  )

  if (success) {
    const topBannerBlock = extractProperty(data.page, "blocks")[0]
    return (
      <BlockRenderer
        blockType={"topBanner"}
        blockData={topBannerBlock}
        components={components}
        props={{ messages, hasBanner: true }}
      >
        <Container>
          <StyledBreadCrumbsContainer>
            <CustomBreadcrumbs current={selfObject} parents={parents} />
          </StyledBreadCrumbsContainer>
        </Container>
        <SuccessModal messages={messages} />
      </BlockRenderer>
    )
  }

  return (
    <>
      <Container>
        <CustomBreadcrumbs current={selfObject} parents={parents} />
      </Container>
      <StyledForm onSubmit={handleSubmit(onSubmit, onError)}>
        {formIsSubmitted && !success && (
          <FailModal messages={messages} setFormIsSubmitted={setFormIsSubmitted} />
        )}
        {blocks?.map((block: any, index: number) => (
          <BlockRenderer
            key={index}
            blockType={block.blockType}
            blockData={block}
            components={components}
            props={
              block.blockType === "formStepTitle"
                ? {
                    index,
                    control,
                    isStepAValid,
                    isStepBValid,
                    isStepCValid,
                    setAtiveAcordion,
                    activeAcordion,
                    errors,
                    watch,
                    formFiles,
                    setFormFiles,
                    handleSubmit,
                    trigger,
                    errorOnFiles,
                    messages,
                  }
                : block.blockType === "contentText"
                ? {
                    hasBanner: hasBanner,
                    control,
                    messages,
                    isStepAValid: () => {
                      if (!watch("IsDonationPolicy") || watch("IsDonationPolicy") === "false") {
                        return false
                      } else return true
                    },
                  }
                : {
                    control,
                    messages,
                    isStepAValid: () => {
                      if (!watch("IsDonationPolicy") || watch("IsDonationPolicy") === "false") {
                        return false
                      } else return true
                    },
                  }
            }
          />
        ))}
        {RECAPTCHA_KEY && (
          <ReCAPTCHA
            ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
            sitekey={RECAPTCHA_KEY}
            size="invisible"
            hl="iw"
            tabIndex={-1}
          />
        )}
      </StyledForm>
    </>
  )
}

export default DonationApplication
